import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { AlertsLimitsApi } from 'Api/AlertsLimits/AlertsLimitsApi';
import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { ControlGroupTargetEffectivenessTabProps, TargetEffectivenessTab, TargetEffectivenessTabType } from 'Components/OperationalControls/TargetEffectivenessTab/TargetEffectivenessTab';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { PrimaryTabs, Tab } from 'Components/Tabs/PrimaryTabs/PrimaryTabs';
import { FRAMEWORKS, GROUPS, OPERATIONAL_CONTROLS } from 'Config/Paths';
import { getFrameworkGroupControlURL } from 'Helpers/URLBuilder/URLBuilder';
import { LimitResponse } from 'Models/Limits';
import { ControlGroupResponse } from 'Models/OperationalControls';

import { ControlGroupAssessmentTab, ControlGroupAssessmentTabProps } from './ControlGroupAssessmentTab/ControlGroupAssessmentTab';
import { ControlGroupConfigurationTab, ControlGroupConfigurationTabProps } from './ControlGroupConfigurationTab/ControlGroupConfigurationTab';
import { ControlGroupLimitsTab, ControlGroupLimitsTabProps } from './ControlGroupLimitsTab/ControlGroupLimitsTab';

interface PathParams {
    controlFramework: string;
    controlGroupId: string;
}

export interface ControlGroupSettingsProps {
    alertsLimitsApi: AlertsLimitsApi;
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    controlsApi: ControlsApi;
}

export interface ControlGroupDeleted {
    isDeleted?: boolean;
}

export const ControlGroupSettings = (props: ControlGroupSettingsProps) => {
    const [isGroupBreadcrumbEnabled, setIsGroupBreadcrumbEnabled] = useState(true);
    const [controlGroupResponse, setDetailedControlGroupResponse] = useState<ControlGroupResponse>();
    const [limitResponseList, setLimitResponseList] = useState<LimitResponse[]>();
    const [isDeleted, setIsDeleted] = useState(false);
    const { controlFramework, controlGroupId } = useParams<keyof PathParams>() as PathParams;

    const getControlGroupConfiguration = useCallback(async (): Promise<void> => {
        try {
            const controlGroupConfigurationResponse = await props.controlsApi.getControlGroupConfiguration(controlFramework, controlGroupId);
            if (controlGroupConfigurationResponse) {
                const controlGroupConfiguration = controlGroupConfigurationResponse.data;
                setDetailedControlGroupResponse(controlGroupConfiguration);
            }
        } catch (err) {
            handleRequestError(err);
        }
    }, [controlFramework, controlGroupId, props.controlsApi]);

    const getControlGroupLimits = useCallback(async (): Promise<void> => {
        try {
            const entity_id = `${controlFramework}#${controlGroupId}`;
            const controlGroupLimitsResponse = await props.alertsLimitsApi.getControlLimits(entity_id);
            const controlGroupLimits = controlGroupLimitsResponse.data;
            setLimitResponseList(controlGroupLimits);
        } catch (error) {
            handleRequestError(error);
        }
    }, [controlFramework, controlGroupId, props.alertsLimitsApi]);

    useEffect(() => {
        getControlGroupConfiguration();
        getControlGroupLimits();
    }, [getControlGroupConfiguration, getControlGroupLimits]);

    const handleRequestError = (error: Error): void => console.error('Error: ', error);

    const onControlGroupDeleted = (): void => {
        setIsGroupBreadcrumbEnabled(false);
        setIsDeleted(true);
    };

    if (controlGroupResponse && limitResponseList) {
        const ControlGroupAssessmentTabProps: ControlGroupAssessmentTabProps & ControlGroupDeleted = {
            controlsApi: props.controlsApi,
            controlGroupResponse: controlGroupResponse,
            isDeleted: isDeleted,
        };

        const controlGroupLimitsTabProps: ControlGroupLimitsTabProps & ControlGroupDeleted = {
            alertsLimitsApi: props.alertsLimitsApi,
            controlGroupResponse: controlGroupResponse,
            getControlGroupLimits: getControlGroupLimits,
            limitResponseList: limitResponseList,
            isDeleted: isDeleted,
        };

        const controlGroupConfigurationTabProps: ControlGroupConfigurationTabProps & ControlGroupDeleted = {
            artificialIntelligenceApi: props.artificialIntelligenceApi,
            onControlGroupSaved: getControlGroupConfiguration,
            onControlGroupDeleted: onControlGroupDeleted,
            controlsApi: props.controlsApi,
            controlGroupResponse: controlGroupResponse,
            isDeleted: isDeleted,
        };

        const controlGroupEffectivenessTargetTabProps: ControlGroupTargetEffectivenessTabProps = {
            _type: TargetEffectivenessTabType.CONTROL_GROUP,
            controlsApi: props.controlsApi,
            controlGroupResponse: controlGroupResponse,
            isDeleted: isDeleted,
            linkOnSuccess: `/${OPERATIONAL_CONTROLS}/${FRAMEWORKS}/${controlGroupResponse.control_framework}/${GROUPS}/${controlGroupResponse.control_group_id}`,
            targetEffectivenessUpdated: getControlGroupConfiguration,
        };

        const defaultActiveTab = controlGroupResponse.is_custom ? 'configuration' : 'assessment';

        return (
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={getFrameworkGroupControlURL(controlGroupResponse.control_framework)}>{controlGroupResponse.control_framework}</BreadcrumbLink>
                        {isGroupBreadcrumbEnabled ? <BreadcrumbLink link={getFrameworkGroupControlURL(`${controlGroupResponse.control_framework}#${controlGroupResponse.control_group_id}`)}>{controlGroupResponse.control_group_name}</BreadcrumbLink> : <BreadcrumbText>{controlGroupResponse.control_group_name}</BreadcrumbText>}
                        <BreadcrumbText>Settings</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={controlGroupResponse.is_custom ? `${controlGroupResponse.control_framework}: ${controlGroupResponse.control_group_name} Settings` : `${controlGroupResponse.control_framework}: ${controlGroupResponse.control_group_id} Settings`}
                headerDescription={controlGroupResponse.control_group_description}
                body={[
                    {
                        content: (
                            <PrimaryTabs defaultActiveTab={defaultActiveTab} removePadding>
                                {controlGroupResponse.is_custom ? (
                                    <Tab eventKey="configuration" title="Configuration">
                                        <ControlGroupConfigurationTab {...controlGroupConfigurationTabProps} />
                                    </Tab>
                                ) : (
                                    <></>
                                )}
                                <Tab title="Assessment" eventKey="assessment">
                                    <ControlGroupAssessmentTab {...ControlGroupAssessmentTabProps} />
                                </Tab>
                                <Tab title="Limits" eventKey="limits">
                                    <ControlGroupLimitsTab {...controlGroupLimitsTabProps} />
                                </Tab>
                                <Tab eventKey="target" title="Target">
                                    <TargetEffectivenessTab {...controlGroupEffectivenessTargetTabProps} />
                                </Tab>
                            </PrimaryTabs>
                        ),
                    },
                ]}
            />
        );
    }

    return null;
};
